import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from 'core/api/api.service';
import { BarclaysResponse } from '../entities/barclays-request.class';

export interface CaptureContextResponse {
  clientLibrary: string;
  clientLibraryIntegrity: string;
  captureContext: string;
}

export interface CaptureContextRequest {
  totalAmount: string;
  clientReferenceInformationCode: string; // Quote/Order number
}

export interface CompletePaymentRequest {
  transientTokenJwt: string;
}

export interface PaymentCompleteResponse {
  status: string;
  paymentIdentifier?: string;
  orderNumber?: string;
  message?: string;
}

export interface ReversePaymentRequest {
  requestId: string;
  clientReferenceInformationCode: string;
  totalAmount: string;
}

export interface SmartPayFuseStatusResponse {
  smartPayFuseActive: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class BarclaysPaymentService {

  private urls = {
    captureContext: '/api/payment/barclaysCaptureContext',
    completePayment: '/api/payment/barclaysCompletePayment',
    reversePayment: '/api/payment/barclaysReversePayment',
    barclaysPaymentResponse: '/api/payment/barclaysCreditCardPayment',
    barclaysResponseUrl: '/api/payment/barclaysResponse',
    smartPayFuseStatus: '/api/payment/smartPayFuseStatus'
  };

  constructor(
    private apiService: ApiService
  ) { }

  /**
   * Get capture context from backend (calls Barclays endpoint)
   * Backend returns a JWT token string, not JSON
   * @param request Payment details: totalAmount
   * @returns Observable that returns JWT token string
   */
  getCaptureContext(request: CaptureContextRequest): Observable<any> {
    return this.apiService.post(this.urls.captureContext, request, { responseType: 'text' });
  }

  /**
   * Complete payment with transient token
   * @param request CompletePaymentRequest containing transient token JWT
   * @returns Observable with payment status response
   */
  completePayment(request: CompletePaymentRequest): Observable<PaymentCompleteResponse> {
    return this.apiService.post(this.urls.completePayment, request);
  }

  reversePayment(request: ReversePaymentRequest): Observable<any> {
    return this.apiService.post(this.urls.reversePayment, request);
  }

  /**
   * Legacy methods for backward compatibility
   */
  sendBarclaysCreditCardPaymentInfo(paymentDetails) {
    return this.apiService.post(this.urls.barclaysResponseUrl, paymentDetails);
  }

  sendBarclaysPaymentResponse(barclaysDetails: BarclaysResponse) {
    return this.apiService.post(this.urls.barclaysPaymentResponse, barclaysDetails);
  }

  getSmartPayFuseStatus(): Observable<SmartPayFuseStatusResponse> {
    return this.apiService.get(this.urls.smartPayFuseStatus);
  }
}
